import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import i18next from 'i18next';
import { v4 as uuid } from 'uuid';
import {
  IPreferencesMenu,
  IStateRequestUpdate,
  PreferencesActionEnum,
  PreferencesMenuItemTypeEnum,
  PreferencesMenuTypeEnum,
  TypeSelectNotificationOptionsEnum,
} from 'Data/interfaces/Preferences/IPreferences';
import {
  IReportEmailSettings,
  ReportPeriodicityEnum,
} from 'Data/interfaces/Preferences/IReport';
import {
  listActivitiesActions,
  listDisciplinesActions,
  listFoldersAndFilesActions,
  listPlottingActions,
  NotificationActionEnum,
} from 'Data/interfaces/Notification/INotification';
import {
  DisciplineNotificationPreferenceEnum,
  FileNotificationPreferenceEnum,
  IPreferences,
  PlottingNotificationPreferenceEnum,
  TopicNotificationPreferenceEnum,
} from 'Data/interfaces/Preferences/IPreferencesApi';
import { useQueryReportEmails } from 'Hooks/report/useQueryReportEmails';
import { useQueryPreferencesEmail } from 'Hooks/preferences/useQueryPreferencesEmail';
import { useQueryPreferences } from 'Hooks/preferences/useQueryPreferences';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { useGlobalizationFlag } from 'Hooks/useGlobalizationFlag';
import { IOptionData } from '../Select';

const useOptions = () => {
  const preferencesFeatureFlag = useFeatureFlag('preferences-feature');
  const preferencesFeature = preferencesFeatureFlag.enabled && preferencesFeatureFlag.value === 'test';
  const { enabled: globalizationEnabled, isLoading: isLoadingGlobalization } = useGlobalizationFlag();

  const {
    data: reportEmailData,
    isFetching: isFetchingReportEmail,
  } = useQueryReportEmails({ enabled: preferencesFeature });
  const {
    data: preferencesNotificationsData,
    isFetching: isFetchingPreferencesNotifications,
  } = useQueryPreferences({
    enabled: preferencesFeature,
  });
  const {
    data: preferencesEmailData,
    isFetching: isFetchingPreferencesEmail,
  } = useQueryPreferencesEmail({
    enabled: preferencesFeature,
  });

  const firstObjOptions = useRef(JSON.parse(JSON.stringify(getDefaultOptions(!isLoadingGlobalization && globalizationEnabled))));
  const [objOptions, setObjOptions] = useState(() => getDefaultOptions(!isLoadingGlobalization && globalizationEnabled));

  const arrayOptions = useMemo(() => {
    return Object.values(objOptions);
  }, [objOptions]);

  const statesIsEquals = useMemo(() => {
    return JSON.stringify(firstObjOptions.current) === JSON.stringify(objOptions);
  }, [objOptions]);

  const handleUpdate = useCallback(({
    Type,
    itemParentId,
    itemChildrenId,
    valueId,
    value,
    isLeft,
    selectType,
  }: IStateRequestUpdate) => {
    setObjOptions((prev) => {
      const itemParentIndex = prev[Type].Items.findIndex((item) => {
        return item.Id === itemParentId;
      });
      if (itemParentIndex !== -1) {
        const itemChildrenIndex = prev[Type].Items[itemParentIndex].Items
          .findIndex((itemChildren) => {
            return itemChildren.Id === itemChildrenId;
          });
        if (itemChildrenIndex !== -1) {
          const valueIndex = prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items
            .findIndex((itemValue) => {
              return itemValue.Id === valueId;
            });
          if (valueIndex !== -1) {
            const prevValue = prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex];
            if (typeof value === 'string' && 'ValueSelect' in prevValue) {
              prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSelect = value;
            }
            if (typeof value === 'boolean') {
              if ('ValueSwitch' in prevValue) {
                if (
                  selectType &&
                  !prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSwitch &&
                  value
                ) {
                  prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSelect = selectType;
                }
                prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueSwitch = value;
              }
              if ('ValueLeft' in prevValue && isLeft) {
                prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueLeft = value;
              }
              if ('ValueRight' in prevValue && !isLeft) {
                prev[Type].Items[itemParentIndex].Items[itemChildrenIndex].Items[valueIndex].ValueRight = value;
              }
            }
          }
        }
      }
      return { ...prev };
    });
  }, []);

  const handleOldData = useCallback(() => {
    setObjOptions(JSON.parse(JSON.stringify(firstObjOptions.current)));
  }, []);

  useEffect(() => {
    const handleFirstPopulated = (
      enabledPreferences?: boolean,
      syncReportEmailData?: IReportEmailSettings,
      enabledPreferencesEmail?: boolean,
      syncPreferencesEmailData?: IPreferences[],
      enabledPreferencesNotifications?: boolean,
      syncPreferencesNotificationsData?: IPreferences[],
    ) => {
      const newObjOptions = getDefaultOptions(!isLoadingGlobalization && globalizationEnabled);
      if (enabledPreferences) {
        newObjOptions[PreferencesMenuTypeEnum.ReportByEmail].IsDisabled = false;
      }
      if (syncReportEmailData) {
        newObjOptions[PreferencesMenuTypeEnum.ReportByEmail].Items.forEach((itemParent) => {
          itemParent.Items.forEach((item) => {
            item.Items.forEach((itemChildren) => {
              if (itemChildren.ParamSwitch && itemChildren.ParamSwitch in syncReportEmailData) {
                itemChildren.ValueSwitch = syncReportEmailData[itemChildren.ParamSwitch as keyof IReportEmailSettings] as boolean;
              }
              if (itemChildren.ParamSelect && itemChildren.ParamSelect in syncReportEmailData) {
                itemChildren.ValueSelect = ReportPeriodicityEnum[syncReportEmailData[itemChildren.ParamSelect as keyof IReportEmailSettings] as any].toString();
              }
            });
          });
        });
      }
      if (enabledPreferencesEmail) {
        newObjOptions[PreferencesMenuTypeEnum.NotificationByEmail].IsDisabled = false;
        newObjOptions[PreferencesMenuTypeEnum.NotificationByEmail].Items.forEach((itemParent) => {
          itemParent.Items.forEach((item) => {
            item.Items.forEach((itemChildren) => {
              itemChildren.ValueSwitchDisabled = false;
              if (syncPreferencesEmailData) {
                const preferenceEmailItem = syncPreferencesEmailData.find((item) => {
                  return item.Action === itemChildren.SelectType as number;
                });
                if (preferenceEmailItem) {
                  if (listFoldersAndFilesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === FileNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = FileNotificationPreferenceEnum[preferenceEmailItem?.Preference]?.toString()
                  }
                  if (listActivitiesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === TopicNotificationPreferenceEnum.None ? false : true;
                    itemChildren.ValueSelectDisabled = false;
                    itemChildren.ValueSelect = TopicNotificationPreferenceEnum[preferenceEmailItem?.Preference]?.toString();
                  }
                  if (listPlottingActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === PlottingNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false;
                    // itemChildren.ValueSelect = PlottingNotificationPreferenceEnum[preferenceEmailItem?.Preference]?.toString();
                  }
                  if (listDisciplinesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceEmailItem.Preference === DisciplineNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = DisciplineNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString()
                  }
                }
              }
            });
          });
        });
      }
      if (enabledPreferencesNotifications) {
        newObjOptions[PreferencesMenuTypeEnum.Notifications].IsDisabled = false;
        newObjOptions[PreferencesMenuTypeEnum.Notifications].Items.forEach((itemParent) => {
          itemParent.Items.forEach((item) => {
            item.Items.forEach((itemChildren) => {
              itemChildren.ValueSwitchDisabled = false;
              if (syncPreferencesNotificationsData) {
                const preferenceNotificationItem = syncPreferencesNotificationsData.find((item) => {
                  return item.Action === itemChildren.SelectType as number;
                });
                if (preferenceNotificationItem) {
                  if (listFoldersAndFilesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === FileNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = FileNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString()
                  }
                  if (listActivitiesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === TopicNotificationPreferenceEnum.None ? false : true;
                    itemChildren.ValueSelectDisabled = false;
                    itemChildren.ValueSelect = TopicNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString();
                  }
                  if (listPlottingActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === PlottingNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false;
                    // itemChildren.ValueSelect = PlottingNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString();
                  }
                  if (listDisciplinesActions.includes(itemChildren.SelectType as NotificationActionEnum)) {
                    itemChildren.ValueSwitch = preferenceNotificationItem.Preference === DisciplineNotificationPreferenceEnum.None ? false : true;
                    // itemChildren.ValueSelectDisabled = false
                    // itemChildren.ValueSelect = DisciplineNotificationPreferenceEnum[preferenceNotificationItem?.Preference]?.toString()
                  }
                }
              }
            });
          });
        });
      }
      firstObjOptions.current = JSON.parse(JSON.stringify(newObjOptions));
      setObjOptions(newObjOptions);
    };

    if (
      reportEmailData && !isFetchingReportEmail &&
      preferencesEmailData && !isFetchingPreferencesEmail &&
      preferencesNotificationsData && !isFetchingPreferencesNotifications
    ) {
      handleFirstPopulated(
        preferencesFeature,
        reportEmailData,
        preferencesFeature,
        preferencesEmailData,
        preferencesFeature,
        preferencesNotificationsData,
      );
    }
  }, [
    preferencesFeature,
    reportEmailData,
    isFetchingReportEmail,
    preferencesEmailData,
    isFetchingPreferencesEmail,
    preferencesNotificationsData,
    isFetchingPreferencesNotifications,
  ]);

  return {
    hasReportEmailData: !!reportEmailData,
    preferencesEmailData,
    hasPreferencesEmailData: preferencesEmailData?.length > 0,
    preferencesNotificationsData,
    hasPreferencesNotificationsData: preferencesNotificationsData?.length > 0,
    listLoading: isFetchingReportEmail || isFetchingPreferencesEmail || isFetchingPreferencesNotifications,
    objOptions,
    arrayOptions,
    statesIsEquals,
    onStateUpdate: handleUpdate,
    onOldData: handleOldData,
  };
};

export default useOptions;

type TypeReportMenuObj = Record<PreferencesMenuTypeEnum, IPreferencesMenu>;

const getDefaultOptions = (globalizationEnabled: boolean): TypeReportMenuObj => ({
  [PreferencesMenuTypeEnum.Notifications]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Notifications,
    Items: [
      {
        Id: uuid(),
        Title: i18next.t('notification:preferences.groups.notificationsHub'),
        Order: 1,
        Items: [
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.foldersAndFiles'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.newFile'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.fileDeleted'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.fileMoved'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileMoved,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: i18next.t('notification:preferences.items.fileMovedToObsolete'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreatedObsolet,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: i18next.t('notification:preferences.items.fileRenamed'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRenamed,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: i18next.t('notification:preferences.items.newFileRevision'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileVersionAdded,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: i18next.t('notification:preferences.items.fileDescriptionChanged'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDescriptionUpdated,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: i18next.t('notification:preferences.items.fileRenderComplete'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRendered,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: i18next.t('notification:preferences.items.fileStatusChanged'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileStatusChanged,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: i18next.t('notification:preferences.items.folderCreated'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderCreated,
              },
              {
                Id: uuid(),
                Order: 11,
                Title: i18next.t('notification:preferences.items.folderRenamed'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderRenamed,
              },
              {
                Id: uuid(),
                Order: 12,
                Title: i18next.t('notification:preferences.items.folderDeleted'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderDeleted,
              },
            ],
          },
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.activities'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 2,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.newActivity'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.activityUpdated'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicUpdated,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.activityStatusChanged'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicStatusChanged,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: i18next.t('notification:preferences.items.activityDeadlineExpiredYesterday'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiredYesterday,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: i18next.t('notification:preferences.items.activityDeadlineTomorrow'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresTommorow,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: i18next.t('notification:preferences.items.activityDeadlineToday'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresToday,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: i18next.t('notification:preferences.items.activityDeleted'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicDeleted,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: i18next.t('notification:preferences.items.fileAttachedToActivity'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentAdded,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: i18next.t('notification:preferences.items.fileDeletedFromActivity'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentDeleted,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: i18next.t('notification:preferences.items.newComment'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicCommentAdded,
              },
            ],
          },
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.disciplines'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 3,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.disciplineCreated'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.disciplineDeleted'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.disciplineRenamed'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineRenamed,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: i18next.t('notification:preferences.items.disciplineDisconnected'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineDisconnected,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: i18next.t('notification:preferences.items.disciplineReconnected'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.DisciplineReconnected,
              },
            ],
          },
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.plotagens'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 4,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.newPlottingRequest'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.plottingDeleted'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingFileDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.plottingRequestDeleted'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingRequestDeleted,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: i18next.t('notification:preferences.items.plottingStatusChanged'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingStatusChanged,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: i18next.t('notification:preferences.items.plottingObsolete'),
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.PlottingCreatedObsolet,
              },
            ],
          },
        ],
      },
    ],
  },
  [PreferencesMenuTypeEnum.NotificationByEmail]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.NotificationByEmail,
    Items: [
      {
        Id: uuid(),
        Title: i18next.t('notification:preferences.groups.notificationEmail'),
        Order: 1,
        Items: [
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.foldersAndFiles'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.newFile'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.fileDeleted'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.fileMoved'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileMoved,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: i18next.t('notification:preferences.items.fileMovedToObsolete'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileCreatedObsolet,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: i18next.t('notification:preferences.items.fileRenamed'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRenamed,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: i18next.t('notification:preferences.items.newFileRevision'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileVersionAdded,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: i18next.t('notification:preferences.items.fileDescriptionChanged'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileDescriptionUpdated,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: i18next.t('notification:preferences.items.fileRenderComplete'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileRendered,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: i18next.t('notification:preferences.items.fileStatusChanged'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FileStatusChanged,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: i18next.t('notification:preferences.items.folderCreated'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderCreated,
              },
              {
                Id: uuid(),
                Order: 11,
                Title: i18next.t('notification:preferences.items.folderRenamed'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderRenamed,
              },
              {
                Id: uuid(),
                Order: 12,
                Title: i18next.t('notification:preferences.items.folderDeleted'),
                ValueSelect: 'AllFolders',
                ValueSelectDisabled: true,
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.FolderDeleted,
              },
            ],
          },
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.activities'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 2,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.newActivity'),
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.TopicCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.activityUpdated'),
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.TopicUpdated,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.activityStatusChanged'),
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.TopicStatusChanged,
              },
              {
                Id: uuid(),
                Order: 4,
                Title: i18next.t('notification:preferences.items.activityDeadlineExpiredYesterday'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiredYesterday,
              },
              {
                Id: uuid(),
                Order: 5,
                Title: i18next.t('notification:preferences.items.activityDeadlineTomorrow'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresTommorow,
              },
              {
                Id: uuid(),
                Order: 6,
                Title: i18next.t('notification:preferences.items.activityDeadlineToday'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicExpiresToday,
              },
              {
                Id: uuid(),
                Order: 7,
                Title: i18next.t('notification:preferences.items.activityDeleted'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicDeleted,
              },
              {
                Id: uuid(),
                Order: 8,
                Title: i18next.t('notification:preferences.items.fileAttachedToActivity'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentAdded,
              },
              {
                Id: uuid(),
                Order: 9,
                Title: i18next.t('notification:preferences.items.fileDeletedFromActivity'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicAttachmentDeleted,
              },
              {
                Id: uuid(),
                Order: 10,
                Title: i18next.t('notification:preferences.items.newComment'),
                ValueSwitch: false,
                ValueSelectDisabled: true,
                ValueSelect: 'All',
                ValueSwitchDisabled: true,
                SelectType: NotificationActionEnum.TopicCommentAdded,
              },
            ],
          },
          {
            Id: uuid(),
            Title: i18next.t('notification:preferences.groups.disciplines'),
            Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
            Order: 3,
            Items: [
              {
                Id: uuid(),
                Order: 1,
                Title: i18next.t('notification:preferences.items.disciplineCreated'),
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.DisciplineCreated,
              },
              {
                Id: uuid(),
                Order: 2,
                Title: i18next.t('notification:preferences.items.disciplineDeleted'),
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.DisciplineDeleted,
              },
              {
                Id: uuid(),
                Order: 3,
                Title: i18next.t('notification:preferences.items.disciplineRenamed'),
                ValueSwitch: false,
                ValueSwitchDisabled: true,
                ValueSelect: 'All',
                ValueSelectDisabled: true,
                SelectType: NotificationActionEnum.DisciplineRenamed,
              },
              // {
              //   Id: uuid(),
              //   Order: 4,
              //   Title: 'Disciplina desconectada',
              //   ValueSelect: 'All',
              //   ValueSelectDisabled: true,
              //   ValueSwitch: false,
              //   ValueSwitchDisabled: true,
              //   SelectType: NotificationActionEnum.DisciplineDisconnected,
              // },
              // {
              //   Id: uuid(),
              //   Order: 5,
              //   Title: 'Disciplina reconectada',
              //   ValueSelect: 'All',
              //   ValueSelectDisabled: true,
              //   ValueSwitch: false,
              //   ValueSwitchDisabled: true,
              //   SelectType: NotificationActionEnum.DisciplineReconnected,
              // },
            ],
          },
          // {
          //   Id: uuid(),
          //   Title: 'Plotagens',
          //   Type: PreferencesMenuItemTypeEnum.CheckAndSelectTable,
          //   Order: 4,
          //   Items: [
          //     {
          //       Id: uuid(),
          //       Order: 1,
          //       Title: 'Nova solicitação de plotagem',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingCreated,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 2,
          //       Title: 'Plotagem excluída',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingFileDeleted,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 3,
          //       Title: 'Solicitação de plotagem excluída',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingRequestDeleted,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 4,
          //       Title: 'Status de plotagem alterado',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingStatusChanged,
          //     },
          //     {
          //       Id: uuid(),
          //       Order: 5,
          //       Title: 'Plotagem obsoleta',
          //       ValueSelect: 'All',
          //       ValueSelectDisabled: true,
          //       ValueSwitch: false,
          //       ValueSwitchDisabled: true,
          //       SelectType: NotificationActionEnum.PlottingCreatedObsolet,
          //     },
          //   ],
          // },
        ],
      },
    ],
  },
  [PreferencesMenuTypeEnum.ReportByEmail]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.ReportByEmail,
    Items: [
      {
        Id: uuid(),
        Title: i18next.t('notification:preferences.groups.fileReport'),
        Order: 1,
        Items: [
          {
            Id: uuid(),
            Title: '',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelect,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                TitleSwitch: i18next.t('notification:preferences.report.fileReceive'),
                DescriptionSwitch: i18next.t('notification:preferences.report.fileReceiveDesc'),
                ParamSwitch: 'ReceiveFileReportEmail',
                ParamRequestSwitch: 'receiveFileReportEmail',
                ValueSwitch: true,
                TitleSelect: i18next.t('notification:preferences.report.filePeriodicity'),
                DescriptionSelect: i18next.t('notification:preferences.report.filePeriodicityDesc'),
                ParamSelect: 'FileReportPeriodicity',
                ParamRequestSelect: 'fileReportPeriodicity',
                ValueSelect: 'Weekly',
                SelectType: PreferencesActionEnum.Periodicity,
                isPeriodicity: true,
                Order: 1,
              },
            ],
          },
        ],
      },
      {
        Id: uuid(),
        Title: i18next.t('notification:preferences.groups.activityReport'),
        Order: 2,
        Items: [
          {
            Id: uuid(),
            Title: '',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelect,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                TitleSwitch: i18next.t('notification:preferences.report.activityReceive'),
                DescriptionSwitch: i18next.t('notification:preferences.report.activityReceiveDesc'),
                ParamSwitch: 'ReceiveTopicReportEmail',
                ParamRequestSwitch: 'receiveTopicReportEmail',
                ValueSwitch: true,
                TitleSelect: i18next.t('notification:preferences.report.activityPeriodicity'),
                DescriptionSelect: i18next.t('notification:preferences.report.activityPeriodicityDesc'),
                ParamSelect: 'TopicReportPeriodicity',
                ParamRequestSelect: 'topicReportPeriodicity',
                ValueSelect: 'Weekly',
                SelectType: PreferencesActionEnum.Periodicity,
                isPeriodicity: true,
                Order: 1,
              },
            ],
          },
        ],
      },
      {
        Id: uuid(),
        Title: i18next.t('notification:preferences.groups.plottingReport'),
        Order: 3,
        Items: [
          {
            Id: uuid(),
            Title: '',
            Type: PreferencesMenuItemTypeEnum.CheckAndSelect,
            Order: 1,
            Items: [
              {
                Id: uuid(),
                TitleSwitch: i18next.t('notification:preferences.report.plottingReceive'),
                DescriptionSwitch: i18next.t('notification:preferences.report.plottingReceiveDesc'),
                ParamSwitch: 'ReceivePlottReportEmail',
                ParamRequestSwitch: 'receivePlottReportEmail',
                ValueSwitch: true,
                TitleSelect: i18next.t('notification:preferences.report.plottingPeriodicity'),
                DescriptionSelect: i18next.t('notification:preferences.report.plottingPeriodicityDesc'),
                ParamSelect: 'PlottReportPeriodicity',
                ParamRequestSelect: 'plottReportPeriodicity',
                ValueSelect: 'Weekly',
                SelectType: PreferencesActionEnum.Periodicity,
                isPeriodicity: true,
                Order: 1,
              },
            ],
          },
        ],
      },
    ],
  },
  [PreferencesMenuTypeEnum.Language]: {
    Id: uuid(),
    IsDisabled: !globalizationEnabled,
    Type: PreferencesMenuTypeEnum.Language,
    Items: [],
  },
  [PreferencesMenuTypeEnum.Accessibility]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Accessibility,
    Items: [],
  },
  [PreferencesMenuTypeEnum.Interface]: {
    Id: uuid(),
    IsDisabled: true,
    Type: PreferencesMenuTypeEnum.Interface,
    Items: [],
  },
});

// TODO: Future implementation
// const selectNotificationsOptions = [
//   'De todas as notificações',
//   'Nenhuma notificação'
// ]

const getSelectPeriodicityOptions = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.daily'), label: i18next.t('notification:preferences.selectOptions.daily'), value: 'Daily' },
  { description: i18next.t('notification:preferences.selectOptions.weekly'), label: i18next.t('notification:preferences.selectOptions.weekly'), value: 'Weekly' },
];

const getSelectFilesOptions = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.allFolders'), label: i18next.t('notification:preferences.selectOptions.allFolders'), value: 'AllFolders' },
  { description: i18next.t('notification:preferences.selectOptions.followedFolders'), label: i18next.t('notification:preferences.selectOptions.followedFolders'), value: 'FollowedFolders' },
  { description: i18next.t('notification:preferences.selectOptions.followedDisciplines'), label: i18next.t('notification:preferences.selectOptions.followedDisciplines'), value: 'FollowedDisciplines' },
  { description: i18next.t('notification:preferences.selectOptions.noNotification'), label: i18next.t('notification:preferences.selectOptions.noNotification'), value: 'None' },
];

const getSelectActivitiesOptions = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.allActivities'), label: i18next.t('notification:preferences.selectOptions.allActivities'), value: 'All' },
  { description: i18next.t('notification:preferences.selectOptions.followedActivities'), label: i18next.t('notification:preferences.selectOptions.followedActivities'), value: 'Followed' },
  { description: i18next.t('notification:preferences.selectOptions.noNotification'), label: i18next.t('notification:preferences.selectOptions.noNotification'), value: 'None' },
];

// TODO: Future implementation
// const selectFullActivitiesOptions: IOptionData[] = [
//   { description: 'De todas as atividades', label: 'De todas as atividades', value: 'All' },
//   { description: 'De atividades seguidas', label: 'De atividades seguidas', value: 'Followed' },
//   { description: 'Apenas menções', label: 'Apenas menções', value: 'Mentions' },
//   { description: 'Nenhuma notificação', label: 'Nenhuma notificação', value: 'None' }
// ]

const getSelectDisciplinesOptions = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.allDisciplines'), label: i18next.t('notification:preferences.selectOptions.allDisciplines'), value: 'All' },
  { description: i18next.t('notification:preferences.selectOptions.followedDisciplines'), label: i18next.t('notification:preferences.selectOptions.followedDisciplines'), value: 'Followed' },
  { description: i18next.t('notification:preferences.selectOptions.noNotification'), label: i18next.t('notification:preferences.selectOptions.noNotification'), value: 'None' },
];

const getSelectDisciplinesOptionsCreated = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.allDisciplines'), label: i18next.t('notification:preferences.selectOptions.allDisciplines'), value: 'All' },
  { description: i18next.t('notification:preferences.selectOptions.noNotification'), label: i18next.t('notification:preferences.selectOptions.noNotification'), value: 'None' },
];

const getSelectPlotagensOptions = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.allPlottings'), label: i18next.t('notification:preferences.selectOptions.allPlottings'), value: 'All' },
  { description: i18next.t('notification:preferences.selectOptions.createdByMe'), label: i18next.t('notification:preferences.selectOptions.createdByMe'), value: 'CreatedByMe' },
  { description: i18next.t('notification:preferences.selectOptions.noNotification'), label: i18next.t('notification:preferences.selectOptions.noNotification'), value: 'None' },
];

const getSelectPlotagensOptionsCreatedByMe = (): IOptionData[] => [
  { description: i18next.t('notification:preferences.selectOptions.allPlottings'), label: i18next.t('notification:preferences.selectOptions.allPlottings'), value: 'All' },
  { description: i18next.t('notification:preferences.selectOptions.noNotification'), label: i18next.t('notification:preferences.selectOptions.noNotification'), value: 'None' },
];

// TODO: Future implementation
// const selectConstructionSitesOptions = [
//   'De todas as obras',
//   'Nenhuma notificação'
// ]

export type TypeSelectNotificationActionOptions = Record<TypeSelectNotificationOptionsEnum, IOptionData[]>;

export const hasSelectNotificationsOptions: TypeSelectNotificationActionOptions = {
  get [NotificationActionEnum.FileCreated]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileDeleted]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileMoved]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileCreatedObsolet]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileRenamed]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileVersionAdded]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileDescriptionUpdated]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileRendered]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FileStatusChanged]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FolderCreated]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FolderRenamed]() { return getSelectFilesOptions(); },
  get [NotificationActionEnum.FolderDeleted]() { return getSelectFilesOptions(); },

  get [NotificationActionEnum.TopicCreated]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicUpdated]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicStatusChanged]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicExpiredYesterday]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicExpiresTommorow]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicExpiresToday]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicDeleted]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicAttachmentAdded]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicAttachmentDeleted]() { return getSelectActivitiesOptions(); },
  get [NotificationActionEnum.TopicCommentAdded]() { return getSelectActivitiesOptions(); },

  get [NotificationActionEnum.PlottingCreated]() { return getSelectPlotagensOptionsCreatedByMe(); },
  get [NotificationActionEnum.PlottingStatusChanged]() { return getSelectPlotagensOptions(); },
  get [NotificationActionEnum.PlottingFileDeleted]() { return getSelectPlotagensOptions(); },
  get [NotificationActionEnum.PlottingRequestDeleted]() { return getSelectPlotagensOptions(); },
  get [NotificationActionEnum.PlottingCreatedObsolet]() { return getSelectPlotagensOptions(); },

  get [NotificationActionEnum.DisciplineCreated]() { return getSelectDisciplinesOptionsCreated(); },
  get [NotificationActionEnum.DisciplineDeleted]() { return getSelectDisciplinesOptions(); },
  get [NotificationActionEnum.DisciplineRenamed]() { return getSelectDisciplinesOptions(); },
  get [NotificationActionEnum.DisciplineDisconnected]() { return getSelectDisciplinesOptions(); },
  get [NotificationActionEnum.DisciplineReconnected]() { return getSelectDisciplinesOptions(); },

  [NotificationActionEnum.BcfImportCompleted]: [],
  [NotificationActionEnum.BcfExportCompleted]: [],

  get [PreferencesActionEnum.Periodicity]() { return getSelectPeriodicityOptions(); },
} as TypeSelectNotificationActionOptions;
